export type BrandId = 'gg' | 'circle' | 'everie'
export type CircleTheme = 'pink' | 'blue'

export interface BrandLogo {
  default: string
  white?: string
  alt?: string
}

export interface BrandColors {
  primary: string
  primaryForeground: string
  accent: string
  background: string
  foreground: string
  border: string
  muted: string
  mutedForeground: string
}

export interface BrandFonts {
  display: string
  body: string
  mono: string
}

export interface ProgramPath {
  title: string
  description: string
  buttonLabel: string
  href: string
  accent?: string // optional per-card accent color
}

export interface BrandFeatures {
  donorDatabase: boolean
  surrogateDatabase: boolean
  calendar: boolean
  circleThemes: boolean
}

export interface BrandConfig {
  id: BrandId
  name: string
  tagline: string
  domain: string
  logo: BrandLogo
  colors: BrandColors
  fonts: BrandFonts
  features: BrandFeatures
  programs: ProgramPath[]
}

export const brands: Record<BrandId, BrandConfig> = {
  gg: {
    id: 'gg',
    name: 'Growing Generations',
    tagline: 'We are a premium-service surrogacy, egg and sperm donation agency.',
    domain: 'app.growinggenerations.com',
    logo: {
      default: '/img/gg-logo-black.webp',
      white: '/img/gg-logo-light.webp',
    },
    colors: {
      primary: '#B8860B',
      primaryForeground: '#FFFFFF',
      accent: '#7D8B6E',
      background: '#F7F3E8',
      foreground: '#2D2D2D',
      border: '#D4C5A9',
      muted: '#EDE6D3',
      mutedForeground: '#6B6B6B',
    },
    fonts: {
      display: '"Playfair Display", Georgia, "Times New Roman", serif',
      body: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      mono: '"JetBrains Mono", "Fira Code", Consolas, monospace',
    },
    features: {
      donorDatabase: true,
      surrogateDatabase: true,
      calendar: true,
      circleThemes: false,
    },
    programs: [
      {
        title: 'Become a Parent',
        description:
          'Over 98% of Growing Generations clients choose to work with the first surrogate they meet. We accomplish this using a highly personalized process that pairs intended parents and surrogates based on preferences, expectations, and personalities.\n\nGrowing Generations offers a match guarantee. If your surrogate does not pass the medical or overall wellness screening, we will cover the screening expenses up to $5,000.\n\nGrowing your Family\'s future means expecting the best.',
        buttonLabel: 'Start Your Journey',
        href: '/form/Intended Parent/ip-prequalification/default',
        accent: '#F5E6D0',
      },
      {
        title: 'Become a Surrogate',
        description:
          'Before completing the surrogate application in its entirety, you will need to take a few minutes to complete a quick survey. If you meet the minimum surrogacy requirements, you will be asked to complete the full application.\n\nBy becoming a surrogate, you can help people become parents despite medical or personal circumstances. You can make parenthood possible and help grow a family.',
        buttonLabel: 'Begin Survey',
        href: '/form/Gestational Surrogate/surrogate-prequalification/default',
        accent: '#A5A58D',
      },
      {
        title: 'Become an Egg Donor',
        description:
          'You will first fill out a simple questionnaire before completing the egg donor application in its entirety. This form allows us to screen for various preliminary qualifications including questions we are required to ask by the Food and Drug Administration (FDA).\n\nAt Growing Generations, we make it as simple as possible to become an egg donor. Each step of the way our specialists will support you and ensure you have a safe, comfortable experience.',
        buttonLabel: 'Begin',
        href: '/form/Egg Donor/eggdonor-prequalification/default',
        accent: '#C9C5B4',
      },
      {
        title: 'Become a Sperm Donor',
        description:
          'You will first fill out a simple questionnaire before completing the sperm donor application in its entirety. This form allows us to screen for various preliminary qualifications including questions we are required to ask by the Food and Drug Administration (FDA).\n\nAt Growing Generations, we make it as simple as possible to become a sperm donor. Each step of the way our specialists will support you and ensure you have a safe, comfortable experience.',
        buttonLabel: 'Begin',
        href: '/form/Sperm Donor/sperm-donor-prequalification/default',
        accent: '#D4C5A9',
      },
    ],
  },
  circle: {
    id: 'circle',
    name: 'Circle Surrogacy',
    tagline: 'Building families through surrogacy with compassion and expertise.',
    domain: 'app.circlesurrogacy.com',
    logo: {
      default: '/brands/circle/logo-circle-pink.svg',
      white: '/brands/circle/logo-circle-white.svg',
      alt: '/brands/circle/logo-circle-blue.svg',
    },
    colors: {
      primary: '#DC54AD',
      primaryForeground: '#FFFFFF',
      accent: '#5D71F4',
      background: '#FFFFFF',
      foreground: '#0A191A',
      border: '#E5E7EB',
      muted: '#F3F4F6',
      mutedForeground: '#6B7280',
    },
    fonts: {
      display: '"Value Sans Pro Bold", sans-serif',
      body: '"Value Sans Pro Regular", "Inter", sans-serif',
      mono: '"JetBrains Mono", "Fira Code", Consolas, monospace',
    },
    features: {
      donorDatabase: false,
      surrogateDatabase: true,
      calendar: true,
      circleThemes: true,
    },
    programs: [
      {
        title: 'Become a Parent',
        description:
          'Start your journey to parenthood with Circle Surrogacy. Our team will guide you through every step of the surrogacy process.',
        buttonLabel: 'Get Started',
        href: '/form/Intended Parent/ip-prequalification/default',
        accent: '#DC54AD',
      },
      {
        title: 'Become a Surrogate',
        description:
          'Help make someone\'s dream of parenthood come true. Complete our quick survey to see if you qualify for our surrogacy program.',
        buttonLabel: 'Apply Now',
        href: '/form/Gestational Surrogate/surrogate-prequalification/default',
        accent: '#5D71F4',
      },
    ],
  },
  everie: {
    id: 'everie',
    name: 'Everie',
    tagline: 'We help build families while making disclosed donation the clear choice.',
    domain: 'app.everie.com',
    logo: {
      default: '/brands/everie/logo-everie.svg',
      white: '/brands/everie/logo-everie-white.svg',
    },
    colors: {
      primary: '#11B14E',
      primaryForeground: '#FFFFFF',
      accent: '#FF931E',
      background: '#FFFFFF',
      foreground: '#000000',
      border: '#EFEFEF',
      muted: '#EFEFEF',
      mutedForeground: '#5E5E5E',
    },
    fonts: {
      display: '"Everie Bold", "Inter", sans-serif',
      body: '"Everie Regular", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      mono: '"JetBrains Mono", "Fira Code", Consolas, monospace',
    },
    features: {
      donorDatabase: true,
      surrogateDatabase: false,
      calendar: false,
      circleThemes: false,
    },
    programs: [
      {
        title: 'Become a Parent',
        description:
          'We thoughtfully offer services to select your ideal donor and define the terms of your shared relationship. Everie creates mutually empowering and transparent experiences.',
        buttonLabel: 'Start Your Journey',
        href: '/form/Intended Parent/ip-prequalification/default',
        accent: '#11B14E',
      },
      {
        title: 'Become an Egg Donor',
        description:
          'Open fair and nurturing opportunities to help build families while being compensated for your valuable contribution. We believe disclosed donation leads to the best outcomes.',
        buttonLabel: 'Apply Now',
        href: '/form/Egg Donor/eggdonor-prequalification/default',
        accent: '#FF931E',
      },
      {
        title: 'Become a Sperm Donor',
        description:
          'Join our community of donors making a meaningful impact. We champion transparency, fair compensation, and equitable relationships between donors and intended parents.',
        buttonLabel: 'Get Started',
        href: '/form/Sperm Donor/sperm-donor-prequalification/default',
        accent: '#037EF3',
      },
    ],
  },
}
